import {chain, isEqual, sortBy, values} from 'lodash';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  ConversationInquiryModel,
  ConversationInquiryResolutionModel,
  ConversationInquirySummarySentenceModel,
  ConversationInquiryTakeawayDetailModel,
} from '../../../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {isCommentActivity} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {ActivityModel, isLinkedConversationActivity} from '../../../../../models/api/activityModel';
import {RelatedKnowledgeReference} from '../../../../../models/api/relatedKnowledgeModel';
import {SimilarInquiry} from '../../../../../models/api/similarInquiriesModel';
import {SynopsisFiltersEnum} from '../../../../../store/panels/viewer/viewerPanelState';

/*
 * Constants.
 */

const MIN_SIGNIFICANCE = 2;

/*
 * Synopsis items.
 */

export enum SynopsisItemTypesEnum {
  MESSAGE = 'message',
  COMMENT = 'comment',
  PINNED_COMMENT = 'pinned_comment',
  LINKED_CONVERSATION = 'linked_conversation',
}

type BaseSynopsisItem = {
  id: string;
  type: SynopsisItemTypesEnum;
  activityId: number;
  timestamp: number;
  shouldAutoCollapse: boolean;
};

export enum SynopsisMessageDetailTypesEnum {
  INQUIRY = 'inquiry',
  RESOLUTION = 'resolution',
  TAKEAWAY = 'takeaway',
}

type BaseSynopsisMessageDetail = {
  id: string;
  summary: ConversationInquirySummarySentenceModel;
  authorId?: number;
  contactUid?: string;
  timestamp: number;
  activityId: number;
  shouldAutoCollapse: boolean;
};

export type SynopsisMessageDetail = BaseSynopsisMessageDetail &
  (
    | {type: Exclude<SynopsisMessageDetailTypesEnum, SynopsisMessageDetailTypesEnum.INQUIRY>}
    | {
        type: SynopsisMessageDetailTypesEnum.INQUIRY;
        isResolved: boolean;
        inquiry: ConversationInquiryModel;
        similarInquiries: ReadonlyArray<SimilarInquiry>;
        relatedKnowledgeReferences: ReadonlyArray<RelatedKnowledgeReference>;
      }
  );

export type SynopsisMessageItem = BaseSynopsisItem & {
  type: SynopsisItemTypesEnum.MESSAGE;
  aiSubject: string;
  contactUid?: string;
  authorId?: number;
  details: ReadonlyArray<SynopsisMessageDetail>;
};

export type SynopsisCommentItem = BaseSynopsisItem & {
  type: SynopsisItemTypesEnum.COMMENT;
  authorIds: ReadonlyArray<number>;
  summary: ConversationInquirySummarySentenceModel;
};

export type SynopsisPinnedActivityItem = BaseSynopsisItem & {
  type: SynopsisItemTypesEnum.PINNED_COMMENT | SynopsisItemTypesEnum.LINKED_CONVERSATION;
};

export type SynopsisItem = SynopsisMessageItem | SynopsisCommentItem | SynopsisPinnedActivityItem;

/*
 * Compute synopsis items based on graph summary.
 * Will render items for each message preview / comment preview / pinned comment.
 */

export function buildSynopsisItems(
  inquiries: ReadonlyArray<ConversationInquiryModel>,
  pinnedActivities: ReadonlyArray<ActivityModel>,
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>>,
  relatedKnowledge: Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>,
): ReadonlyArray<SynopsisItem> {
  const messageItems = buildMessageItems(inquiries, similarInquiries, relatedKnowledge);
  const commentItems = buildCommentItems(inquiries);
  const pinnedActivityItems = buildPinnedActivityItems(pinnedActivities);

  // Exclude insights from pinned comments so they don't appear as duplicates.
  const pinnedActivityIds = pinnedActivities.map(({id}) => id);
  const unpinnedCommentItems = commentItems.filter(({activityId}) => !pinnedActivityIds.includes(activityId));

  return sortBy([...messageItems, ...unpinnedCommentItems, ...pinnedActivityItems], (item) => item.timestamp);
}

function buildPinnedActivityItems(
  pinnedActivities: ReadonlyArray<ActivityModel>,
): ReadonlyArray<SynopsisItem> {
  return pinnedActivities
    .map((activity) => {
      const type = getPinnedItemType(activity);
      if (!type) {
        return undefined;
      }

      const item: SynopsisPinnedActivityItem = {
        id: `pinned-activity-${activity.id}`,
        type,
        activityId: activity.id,
        timestamp: activity.date,
        shouldAutoCollapse: false,
      };

      return item;
    })
    .filter(isDefined);
}

function getPinnedItemType(activity: ActivityModel) {
  if (isCommentActivity(activity)) {
    return SynopsisItemTypesEnum.PINNED_COMMENT;
  }

  if (isLinkedConversationActivity(activity)) {
    return SynopsisItemTypesEnum.LINKED_CONVERSATION;
  }

  return undefined;
}

function buildMessageItems(
  inquiries: ReadonlyArray<ConversationInquiryModel>,
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>>,
  relatedKnowledge: Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>,
): ReadonlyArray<SynopsisMessageItem> {
  return chain(inquiries)
    .map((inquiry) => {
      const {resolution} = inquiry;

      const takeawayDetails = inquiry.takeaways
        .map(({details}) => details)
        .flat()
        .filter(({source}) => source === 'external') // from messages.
        .map((detail) => buildMessageTakeawayDetail(detail, inquiry));

      return [
        buildInquiryDetail(inquiry, similarInquiries, relatedKnowledge),
        resolution ? buildResolutionDetail(resolution) : undefined,
        ...takeawayDetails,
      ].filter(isDefined);
    })
    .flatten()
    .groupBy((detail) => detail.activityId) // group by message.
    .values()
    .map(buildMessageItem)
    .value();
}

function buildCommentItems(
  inquiries: ReadonlyArray<ConversationInquiryModel>,
): ReadonlyArray<SynopsisCommentItem> {
  return inquiries
    .map((inquiry) =>
      inquiry.takeaways
        .map(({details}) => details)
        .flat()
        .filter(({source}) => source === 'internal') // from comments.
        .map((detail) => buildCommentItem(detail, inquiry)),
    )
    .flat();
}

function buildInquiryDetail(
  inquiry: ConversationInquiryModel,
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>>,
  relatedKnowledge: Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>,
): SynopsisMessageDetail {
  return {
    id: inquiry.identifier,
    type: SynopsisMessageDetailTypesEnum.INQUIRY,
    summary: inquiry.summary,
    timestamp: inquiry.startedAt,
    activityId: inquiry.activityId,
    authorId: inquiry.teammateIds[0],
    contactUid: inquiry.contactUids[0],
    shouldAutoCollapse: false,
    isResolved: inquiry.isResolved,
    inquiry,
    similarInquiries: similarInquiries[inquiry.identifier] ?? [],
    relatedKnowledgeReferences: relatedKnowledge[inquiry.identifier] ?? [],
  };
}

function buildResolutionDetail(resolution: ConversationInquiryResolutionModel): SynopsisMessageDetail {
  return {
    id: resolution.identifier,
    type: SynopsisMessageDetailTypesEnum.RESOLUTION,
    summary: resolution.summary,
    authorId: resolution.teammateIds[0],
    contactUid: resolution.contactUids[0],
    timestamp: resolution.timestamp,
    activityId: resolution.activityId,
    shouldAutoCollapse: false,
  };
}

function buildMessageTakeawayDetail(
  takeawayDetail: ConversationInquiryTakeawayDetailModel,
  inquiry: ConversationInquiryModel,
): SynopsisMessageDetail {
  return {
    id: takeawayDetail.identifier,
    type: SynopsisMessageDetailTypesEnum.TAKEAWAY,
    summary: takeawayDetail.summary,
    authorId: takeawayDetail.teammateIds[0],
    contactUid: takeawayDetail.contactUids[0],
    timestamp: takeawayDetail.timestamp,
    activityId: takeawayDetail.activityIds[0],
    shouldAutoCollapse: takeawayDetail.significanceScore <= MIN_SIGNIFICANCE || inquiry.isResolved,
  };
}

function buildMessageItem(details: ReadonlyArray<SynopsisMessageDetail>): SynopsisMessageItem {
  const firstDetail = details[0];

  return {
    id: firstDetail.id,
    type: SynopsisItemTypesEnum.MESSAGE,
    aiSubject: firstDetail.summary.subject,
    authorId: firstDetail.authorId,
    contactUid: firstDetail.contactUid,
    activityId: firstDetail.activityId,
    timestamp: firstDetail.timestamp,
    details,
    shouldAutoCollapse: details.every((detail) => detail.shouldAutoCollapse),
  };
}

function buildCommentItem(
  takeawayDetail: ConversationInquiryTakeawayDetailModel,
  inquiry: ConversationInquiryModel,
): SynopsisCommentItem {
  return {
    id: takeawayDetail.identifier,
    type: SynopsisItemTypesEnum.COMMENT,
    timestamp: takeawayDetail.timestamp,
    activityId: takeawayDetail.activityIds[0],
    authorIds: takeawayDetail.teammateIds,
    summary: takeawayDetail.summary,
    shouldAutoCollapse: takeawayDetail.significanceScore <= MIN_SIGNIFICANCE || inquiry.isResolved,
  };
}

/*
 * Synopsis item groups. If a group contains several items:
 * - Either they are all collapsed.
 * - Or they are follow-ups: they are all from the same author and the author name won't be repeated.
 */

export interface SynopsisItemGroup {
  id: string;
  isCollapsed: boolean;
  items: ReadonlyArray<SynopsisItem>;
  resolution?: ConversationInquiryResolutionModel;
}

// Group items so we can render collapsed groups as a single block.
export function buildSynopsisItemGroups(
  synopsisItems: ReadonlyArray<SynopsisItem>,
  collapsedItems: Record<string, boolean>,
): ReadonlyArray<SynopsisItemGroup> {
  let group: SynopsisItemGroup | undefined;
  const results: Array<SynopsisItemGroup> = [];

  synopsisItems.forEach((item) => {
    if (group && shouldAddToGroup(group, item, collapsedItems)) {
      group.items = [...group.items, item];
    } else {
      if (group) {
        results.push(group);
      }

      group = {
        id: `group-${item.id}`,
        isCollapsed: collapsedItems[item.id] ?? item.shouldAutoCollapse,
        items: [item],
      };
    }
  });

  if (group) {
    results.push(group);
  }

  return results;
}

function shouldAddToGroup(
  group: SynopsisItemGroup,
  addedItem: SynopsisItem,
  collapsedItems: Record<string, boolean>,
): boolean {
  const isItemCollapsed = collapsedItems[addedItem.id] ?? addedItem.shouldAutoCollapse;
  if (group.isCollapsed !== isItemCollapsed) {
    return false;
  }

  // Always group collapsed items together.
  if (group.isCollapsed && isItemCollapsed) {
    return true;
  }

  const firstItem = group.items[0];
  if (!firstItem) {
    return true;
  }

  if (firstItem.type === SynopsisItemTypesEnum.MESSAGE && addedItem.type === SynopsisItemTypesEnum.MESSAGE) {
    if (firstItem.authorId) {
      return firstItem.authorId === addedItem.authorId;
    }

    if (firstItem.contactUid) {
      return firstItem.contactUid === addedItem.contactUid;
    }

    return firstItem.aiSubject === addedItem.aiSubject;
  }

  if (firstItem.type === SynopsisItemTypesEnum.COMMENT && addedItem.type === SynopsisItemTypesEnum.COMMENT) {
    return isEqual(firstItem.authorIds, addedItem.authorIds);
  }

  return false;
}

/*
 * Stats for data events.
 */

export function buildSynopsisDataEvent(
  filter: SynopsisFiltersEnum | undefined,
  inquiries: ReadonlyArray<ConversationInquiryModel> | undefined,
  pinnedActivities: ReadonlyArray<ActivityModel> | undefined,
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>> | undefined,
  relatedKnowledge:
    | Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>
    | undefined,
) {
  if (!inquiries || !pinnedActivities || !relatedKnowledge) {
    return {};
  }

  return {
    entrypoint: filter ?? 'oneliner',
    num_inquiries: inquiries.length,
    num_ongoing_inquiries: inquiries.filter((inquiry) => !inquiry.isResolved).length,
    num_resolved_inquiries: inquiries.filter((inquiry) => inquiry.isResolved).length,
    num_resolutions: inquiries.filter((inquiry) => isDefined(inquiry.resolution)).length,
    num_takeaways: countTakeawayDetails(inquiries),
    num_pinned_comments: pinnedActivities.length,
    num_collapsed: countTakeawayDetails(
      inquiries,
      (detail, inquiry) => inquiry.isResolved || detail.significanceScore <= MIN_SIGNIFICANCE,
    ),
    num_similar_inquiries: values(similarInquiries).reduce(
      (num, similarInquiriesList) => num + similarInquiriesList.length,
      0,
    ),
    num_references: values(relatedKnowledge).reduce((num, referenceList) => num + referenceList.length, 0),
  };
}

function countTakeawayDetails(
  inquiries: ReadonlyArray<ConversationInquiryModel>,
  predicate: (
    detail: ConversationInquiryTakeawayDetailModel,
    inquiry: ConversationInquiryModel,
  ) => boolean = () => true,
) {
  return inquiries.reduce(
    (num, inquiry) =>
      num +
      inquiry.takeaways.flatMap(({details}) => details).filter((detail) => predicate(detail, inquiry)).length,
    0,
  );
}
